import { useMemo } from "react";
import { useQueries } from "@tanstack/react-query";
import { QUERY_KEYS } from "@/machines/api/config.ts";
import { Machine } from "@/machines/types/machine.ts";
import { TruckEventOverview } from "@/machines/types/event.ts";
import { getTruckEventsOverview } from "@/machines/api/requests/events.requests.ts";

export interface UseAggregatedMetadataParams {
  deviceMxIds: Array<Machine["device_mxid"]>;
  start: Date;
  end: Date;
}

export interface AggregatedMetadata {
  truckLoading: {
    avgCycleTime: number;
    longCyclesCnt: number;
    longCyclesPerc: number;
    vsTarget: number;
    targetCycleTime: number;
    bucketCount: number;
    boulderCount: number;
    boulderImpact: number;
  };
  waitingForTruck: {
    avgWaitTime: number;
    longWaitsCnt: number;
    longWaitsPerc: number;
    targetWaitingTime: number;
  };
}

export interface AggregatedMetadataResult {
  aggregated: AggregatedMetadata;
  perMachine: Array<{ device_mxid?: string; metadata: TruckEventOverview["metadata"] | null }>;
}

/**
 * NOTE: the query key + page size below are shared verbatim with
 * `@/map/hooks/useMachineKpis` (`OVERVIEW_PAGE_SIZE`), so the map rail's KPIs
 * and this aggregate are served by ONE request per machine. Change one and you
 * must change the other, or the `/map` page fetches this endpoint twice.
 */
export const useAggregatedMetadata = ({ deviceMxIds, start, end }: UseAggregatedMetadataParams) => {
  const queries = useQueries({
    queries: (deviceMxIds ?? []).map((mxid) => ({
      queryKey: [QUERY_KEYS.TRUCK_EVENTS_OVERVIEW, { device_mxid: mxid, start_time: start, end_time: end, page: 1, itemsPerPage: 3000 }],
      queryFn: () => getTruckEventsOverview({ device_mxid: mxid, start_time: start, end_time: end, page: 1, itemsPerPage: 3000 }),
      enabled: !!mxid && !!start && !!end,
      staleTime: 1000 * 60 * 60 * 12,
    })),
  });

  const isLoading = queries.some((q) => q.status === "pending");

  const result = useMemo<AggregatedMetadataResult | null>(() => {
    const responses = queries
      .map((q, idx) => ({
        device_mxid: deviceMxIds[idx],
        metadata: q.data?.data?.metadata ?? null,
      }));

    const valid = responses.filter((r) => !!r.metadata) as Array<{ device_mxid?: string; metadata: TruckEventOverview["metadata"] }>;
    const count = valid.length || 1;

    // Initialize accumulators for truckLoading
    let sumAvgCycleTime = 0;
    let sumLongCyclesCnt = 0;
    let sumLongCyclesPerc = 0;
    let sumVsTarget = 0;
    let sumTargetCycleTime = 0;
    let sumBucketCount = 0;
    let sumBoulderCount = 0;
    let sumBoulderImpact = 0;

    // Initialize accumulators for waitingForTruck
    let sumAvgWaitTime = 0;
    let sumLongWaitsCnt = 0;
    let sumLongWaitsPerc = 0;
    let sumTargetWaitingTime = 0;

    valid.forEach(({ metadata }) => {
      const { truckLoading, waitingForTruck } = metadata;

      // Aggregate truckLoading
      sumAvgCycleTime += truckLoading.avgCycleTime ?? 0;
      sumLongCyclesCnt += truckLoading.longCyclesCnt ?? 0;
      sumLongCyclesPerc += truckLoading.longCyclesPerc ?? 0;
      sumVsTarget += truckLoading.vsTarget ?? 0;
      sumTargetCycleTime += truckLoading.targetCycleTime ?? 0;
      sumBucketCount += truckLoading.bucketCount ?? 0;
      sumBoulderCount += truckLoading.boulderCount ?? 0;
      sumBoulderImpact += truckLoading.boulderImpact ?? 0;

      // Aggregate waitingForTruck
      sumAvgWaitTime += waitingForTruck.avgWaitTime ?? 0;
      sumLongWaitsCnt += waitingForTruck.longWaitsCnt ?? 0;
      sumLongWaitsPerc += waitingForTruck.longWaitsPerc ?? 0;
      sumTargetWaitingTime += waitingForTruck.targetWaitingTime ?? 0;
    });

    const aggregated: AggregatedMetadata = {
      truckLoading: {
        avgCycleTime: sumAvgCycleTime / count,
        longCyclesCnt: sumLongCyclesCnt, // total count
        longCyclesPerc: sumLongCyclesPerc / count,
        vsTarget: sumVsTarget / count,
        targetCycleTime: sumTargetCycleTime / count,
        bucketCount: sumBucketCount, // total count
        boulderCount: sumBoulderCount, // total count
        boulderImpact: sumBoulderImpact / count,
      },
      waitingForTruck: {
        avgWaitTime: sumAvgWaitTime / count,
        longWaitsCnt: sumLongWaitsCnt, // total count
        longWaitsPerc: sumLongWaitsPerc / count,
        targetWaitingTime: sumTargetWaitingTime / count,
      },
    };

    return {
      aggregated,
      perMachine: responses,
    };
  }, [queries, deviceMxIds]);

  return { isLoading, data: result };
};
